import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { BannerComponent } from '../../../../../shared/components/banner/banner.component';
import { environment } from '../../../../../../environments';
import { InsuranceService } from '../../../../../core/services/insurance.service';
import { InsuranceContract } from '../../../../../core/interfaces/insurance.interface';
import { AuthService } from '../../../../../core/services/auth.service';
import { AuthorizationService } from '../../../../../core/services/authorization.service';
import {
  LucideAngularModule,
  UsersRound,
  Files,
  MessageCircleMore,
  Menu,
  X as CloseIcon,
  History,
  Database,
  ChartBar,
} from 'lucide-angular';
import { InsuranceBeneficiariesComponent } from '../../../components/insurance-beneficiaries/insurance-beneficiaries.component';
import {
  MedicalHistoryComponent,
  Beneficiary as MedicalHistoryBeneficiary,
} from '../../../components/medical-history';
import {
  CareNetworksComponent,
  CareNetwork,
} from '../../../components/care-networks';
import { MessagesComponent } from '../../../components/messages';
import { DocumentsComponent } from '../../../components/documents';
import { Country } from '../../../../../core/interfaces/doctor.interface';
import { FrNumberPipe } from '../../../../../shared/pipes/fr-number.pipe';
import { DashboardService } from '../../../../../core/services/dashboard.service';
import { UserService } from '../../../../../core/services/user.service';
import { Subscription } from 'rxjs';

// Interface pour les bénéficiaires
export interface Beneficiary {
  _id: string;
  contractId: string;
  userId: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    passportNumber: string;
    birthday: string;
    sex: string;
    phoneNumbers: any[];
  };
  eyoneExternalId: string;
  eyoneInternalId: string;
  beneficiaryExternalId: string;
  type: string;
  matricule: string;
  isActive: boolean;
  isPrincipalInsured: boolean;
  medicalCare: {
    careLimit: number;
    carePercentage: number;
  };
  prestationTypes: Record<string, boolean>;
  prestations: {
    label: string;
    code: string;
    pourcentage: number;
    medicalAct: boolean;
  }[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

@Component({
  selector: 'app-insurances-details',
  standalone: true,
  imports: [
    CommonModule,
    BannerComponent,
    LucideAngularModule,
    InsuranceBeneficiariesComponent,
    MedicalHistoryComponent,
    CareNetworksComponent,
    MessagesComponent,
    DocumentsComponent,
    FrNumberPipe,
  ],
  templateUrl: './details.component.html',
})
export class InsurancesDetailsComponent implements OnInit, OnDestroy {
  readonly Menu = Menu;
  readonly CloseIcon = CloseIcon;
  isMenuOpen = false;
  isDppTheme = environment.theme.default === 'dpp';
  selectedCountry: Country | null = null;

  // Données du contrat
  contract: InsuranceContract | null = null;
  loading = false;
  error: string | null = null;
  contractId: string | null = null;

  // Données des bénéficiaires
  beneficiaries: Beneficiary[] = [];
  beneficiariesLoading = false;
  beneficiariesError: string | null = null;

  // Bénéficiaires pour le composant Documents
  documentsBeneficiaries: MedicalHistoryBeneficiary[] = [];

  // Bénéficiaires pour le composant MedicalHistory (prestations)
  medicalHistoryBeneficiaries: MedicalHistoryBeneficiary[] = [];

  // ID de police externe pour le composant MedicalHistory
  policeExternalId: string | undefined = undefined;

  // Données des réseaux de soins
  careNetworks: CareNetwork[] = [];
  careNetworksLoading = false;
  careNetworksError: string | null = null;

  links = [
    { label: 'Votre passeport', href: '/passport' },
    { label: 'Contrats', href: '/passport/insurances' },
    { label: '', href: undefined },
  ];
  insuranceMenu = [
    {
      key: 'contract_dashboard',
      label: 'Tableau de bord',
      icon: ChartBar,
    },
    {
      key: 'beneficiaries',
      label: 'Bénéficiaires',
      icon: UsersRound,
    },
    {
      key: 'financial_documents',
      label: 'Documents financiers',
      icon: Files,
    },
    {
      key: 'prestations',
      label: 'Prestations',
      icon: History,
    },
    {
      key: 'messages',
      label: 'Messagerie',
      icon: MessageCircleMore,
    },
    {
      key: 'care_networks',
      label: 'Réseaux de soins',
      icon: Database,
    },
  ];
  selectedMenu = this.insuranceMenu[0];

  // Statistiques du contrat (Tableau de bord)
  contractStatistics: any | null = null;
  contractStatisticsLoading = false;
  contractStatisticsError: string | null = null;

  // Montant à rembourser pour ce contrat (depuis user-stats.amountToReimburse.byContract)
  amountToBeReimbursed: number | null = null;
  amountToBeReimbursedLoading = false;

  // Sous-comptes
  subAccounts: any[] = [];
  subAccountsLoading = false;
  subAccountsError: string | null = null;

  // Subscription pour écouter les changements de navigation
  private navigationSubscription?: Subscription;

  constructor(
    private insuranceService: InsuranceService,
    private route: ActivatedRoute,
    private dashboardService: DashboardService,
    private authService: AuthService,
    private userService: UserService,
    private authorizationService: AuthorizationService,
    private router: Router // Ajout injection router
  ) {}

  ngOnInit(): void {
    // Récupérer le pays sélectionné depuis le localStorage
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          this.selectedCountry = JSON.parse(saved);
          // Mettre à jour les liens avec le code du pays
          this.updateLinks();
        } catch {
          this.selectedCountry = null;
        }
      }
    }

    // Récupérer les sous-comptes
    this.fetchSubAccounts();

    // Réinitialiser isMenuOpen à false à chaque navigation
    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isMenuOpen = false;
      }
    });

    this.route.paramMap.subscribe((params) => {
      this.contractId = params.get('contractId');
      if (this.contractId) {
        this.fetchContractDetails();
        this.fetchBeneficiaries();
        this.fetchContractStatistics();
        this.fetchAmountToReimburse();
        this.fetchDocumentsBeneficiaries();
        this.fetchMedicalHistoryBeneficiaries();
      } else {
        this.error = 'Aucun contrat sélectionné.';
      }
    });
  }

  ngOnDestroy(): void {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  fetchSubAccounts(): void {
    this.subAccountsLoading = true;
    this.subAccountsError = null;

    this.userService.getSubAccounts().subscribe({
      next: (response) => {
        this.subAccounts = response.subAccounts || [];
        this.subAccountsLoading = false;
        console.log('Sous-comptes récupérés:', this.subAccounts);
      },
      error: (err) => {
        console.error('Erreur lors du chargement des sous-comptes:', err);
        this.subAccountsError = 'Erreur lors du chargement des sous-comptes.';
        this.subAccountsLoading = false;
      },
    });
  }

  private updateLinks(): void {
    if (this.selectedCountry?.code) {
      this.links[0].href = `/${this.selectedCountry.code}/passport`;
      this.links[1].href = `/${this.selectedCountry.code}/passport/insurances`;

      // Mettre à jour le label du dernier élément avec le nom du garant si disponible
      if (this.contract?.insurer?.name) {
        this.links[2].label = this.contract.insurer.name;
      }
    }
  }

  fetchContractDetails(): void {
    if (!this.contractId) return;

    this.loading = true;
    this.error = null;

    this.insuranceService.getContractDetails(this.contractId).subscribe({
      next: (contract) => {
        this.contract = contract;
        this.loading = false;

        // Récupérer l'ID de police externe pour le composant MedicalHistory
        // Utiliser policeExternalId s'il existe, sinon utiliser policeCustomId
        this.policeExternalId =
          contract?.policeExternalId || contract?.policeCustomId || undefined;

        // Mettre à jour les liens après avoir récupéré les détails du contrat
        this.updateLinks();
      },
      error: (err) => {
        console.error('Erreur lors du chargement des détails du contrat:', err);
        this.error =
          'Erreur lors du chargement des détails du contrat. Veuillez réessayer.';
        this.loading = false;
      },
    });
  }

  fetchBeneficiaries(): void {
    if (!this.contractId) return;

    this.beneficiariesLoading = true;
    this.beneficiariesError = null;

    this.insuranceService.getContractBeneficiaries(this.contractId).subscribe({
      next: (data) => {
        const all = data.beneficiaries || [];
        this.beneficiaries = all
          .map((beneficiary: any) => ({
            ...beneficiary,
            prestations: (beneficiary.prestations || []).map(
              (prestation: any) => ({
                ...prestation,
                medicalAct: prestation.medicalAct || false, // S'assurer que medicalAct existe
              })
            ),
          }))
          .sort(
            (a: Beneficiary, b: Beneficiary) =>
              (b.isPrincipalInsured ? 1 : 0) - (a.isPrincipalInsured ? 1 : 0)
          );

        this.beneficiariesLoading = false;
      },
      error: (err) => {
        console.error('Erreur lors du chargement des bénéficiaires:', err);
        this.beneficiariesError =
          'Erreur lors du chargement des bénéficiaires. Veuillez réessayer.';
        this.beneficiariesLoading = false;
      },
    });
  }

  fetchDocumentsBeneficiaries(): void {
    if (!this.contractId) return;

    this.authorizationService.getAccessibleUsers().subscribe({
      next: (response) => {
        const currentUser = this.authService.getUserData();
        const currentUserPassportNumber = currentUser?.passportNumber;

        // Filtrer les utilisateurs pour les documents
        const filteredUsers = response.users.filter((user: any) => {
          // Critère 1: Même passeport que l'utilisateur connecté
          if (user.passportNumber === currentUserPassportNumber) {
            return true;
          }

          // Critère 2: Vérifier les autorisations pour documents
          return user.authorizations.some((auth: any) => {
            // Vérifier si le statut est 'active'
            if (auth.status !== 'active') {
              return false;
            }

            // Vérifier s'il y a des permissions wildcard (accès total)
            const hasWildcardPermission = auth.permissions.some(
              (perm: any) =>
                perm.resourceType === '*' && 
                perm.actions.includes('*') &&
                (perm.scope === 'all' || !perm.scope)
            );

            if (hasWildcardPermission) {
              return true;
            }

            // Vérifier s'il y a une permission pour documents avec scope financial
            const hasDocumentsPermission = auth.permissions.some(
              (perm: any) =>
                perm.resourceType === 'documents' &&
                perm.actions.includes('read') &&
                perm.scope === 'financial'
            );

            // Vérifier s'il y a une permission pour insurance_contracts avec le bon resourceId
            const hasContractPermission = auth.permissions.some(
              (perm: any) =>
                (perm.resourceType === 'insurance_contract' || perm.resourceType === 'insurance_contracts') &&
                perm.resourceId === this.contractId &&
                perm.actions.includes('read') &&
                perm.scope === 'financial'
            );

            return hasDocumentsPermission && hasContractPermission;
          });
        });

        // Mapper les utilisateurs filtrés vers le format MedicalHistoryBeneficiary
        this.documentsBeneficiaries = filteredUsers.map((user: any) => ({
          fullname: `${user.firstName} ${user.lastName}`,
          passportNumber: user.passportNumber,
        }));

        console.log('Bénéficiaires filtrés pour Documents:', this.documentsBeneficiaries);
      },
      error: (err) => {
        console.error('Erreur lors du chargement des utilisateurs accessibles pour documents:', err);
      },
    });
  }

  fetchMedicalHistoryBeneficiaries(): void {
    if (!this.contractId) return;

    this.authorizationService.getAccessibleUsers().subscribe({
      next: (response) => {
        const currentUser = this.authService.getUserData();
        const currentUserPassportNumber = currentUser?.passportNumber;

        // Filtrer les utilisateurs pour l'historique médical (prestations)
        const filteredUsers = response.users.filter((user: any) => {
          // Critère 1: Même passeport que l'utilisateur connecté
          if (user.passportNumber === currentUserPassportNumber) {
            return true;
          }

          // Critère 2: Vérifier les autorisations pour prestations
          return user.authorizations.some((auth: any) => {
            // Vérifier si le statut est 'active'
            if (auth.status !== 'active') {
              return false;
            }

            // Vérifier s'il y a des permissions wildcard (accès total)
            const hasWildcardPermission = auth.permissions.some(
              (perm: any) =>
                perm.resourceType === '*' && 
                perm.actions.includes('*') &&
                (perm.scope === 'all' || !perm.scope)
            );

            if (hasWildcardPermission) {
              return true;
            }

            // Vérifier s'il y a une permission pour prestations avec scope financial
            const hasPrestationsPermission = auth.permissions.some(
              (perm: any) =>
                perm.resourceType === 'prestations' &&
                perm.actions.includes('read') &&
                perm.scope === 'financial'
            );

            // Vérifier s'il y a une permission pour insurance_contracts avec le bon resourceId
            const hasContractPermission = auth.permissions.some(
              (perm: any) =>
                (perm.resourceType === 'insurance_contract' || perm.resourceType === 'insurance_contracts') &&
                perm.resourceId === this.contractId &&
                perm.actions.includes('read') &&
                perm.scope === 'financial'
            );

            return hasPrestationsPermission && hasContractPermission;
          });
        });

        // Mapper les utilisateurs filtrés vers le format MedicalHistoryBeneficiary
        this.medicalHistoryBeneficiaries = filteredUsers.map((user: any) => ({
          fullname: `${user.firstName} ${user.lastName}`,
          passportNumber: user.passportNumber,
        }));

        console.log('Bénéficiaires filtrés pour MedicalHistory:', this.medicalHistoryBeneficiaries);
      },
      error: (err) => {
        console.error('Erreur lors du chargement des utilisateurs accessibles pour prestations:', err);
      },
    });
  }

  fetchContractStatistics(): void {
    if (!this.contractId) return;
  
    this.contractStatisticsLoading = true;
    this.contractStatisticsError = null;
  
    this.dashboardService.getContractStatistics(this.contractId).subscribe({
      next: (stats) => {
        this.contractStatistics = stats;
        this.contractStatisticsLoading = false;
      },
      error: (err) => {
        console.error('Erreur lors du chargement des statistiques du contrat:', err);
        this.contractStatisticsError =
          'Erreur lors du chargement des statistiques. Veuillez réessayer.';
        this.contractStatisticsLoading = false;
      },
    });
  }

  fetchAmountToReimburse(): void {
    if (!this.contractId) return;

    this.amountToBeReimbursedLoading = true;
    this.amountToBeReimbursed = null;

    this.dashboardService.getUserStats().subscribe({
      next: (stats) => {
        const byContract = stats.amountToReimburse?.byContract;
        const entry = byContract?.find((c) => c.contractId === this.contractId);
        this.amountToBeReimbursed = entry != null ? entry.value : 0;
        this.amountToBeReimbursedLoading = false;
      },
      error: (err) => {
        console.error('Erreur lors du chargement du montant à rembourser:', err);
        this.amountToBeReimbursed = 0;
        this.amountToBeReimbursedLoading = false;
      },
    });
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  @ViewChild(MessagesComponent) messagesComponent!: MessagesComponent;

  openNewMessage() {
    if (this.messagesComponent) {
      this.messagesComponent.openNewMessageForm();
    }
  }
}
